import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useUser } from '@/contexts/UserContext'
import { useVersion } from '@/hooks/use-version'
import {
  ClipboardList,
  Ticket,
  Lightbulb,
  Keyboard,
  Sparkles,
  LifeBuoy,
  ChevronRight,
} from 'lucide-react'

interface GetMoreHelpRailProps {
  /** Open an in-page help section (e.g. keyboard shortcuts, what's new). */
  onOpenSection: (sectionId: string) => void
}

/**
 * Always-visible escalation panel for the Help Center. Surfaces the real support
 * channels (internal support request, new ticket, feature request) plus quick links
 * to the keyboard-shortcuts and What's New help, and the running app version.
 */
export default function GetMoreHelpRail({ onOpenSection }: GetMoreHelpRailProps) {
  const { isAdmin, canManageTickets } = useUser()
  const { data: version } = useVersion()

  const linkClass =
    'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <LifeBuoy className="h-5 w-5 text-primary" aria-hidden="true" />
          Get more help
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <Link to="/support-request" className={linkClass}>
          <span className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Submit a support request
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </Link>

        {canManageTickets && (
          <Link to="/tickets/new" className={linkClass}>
            <span className="flex items-center gap-2">
              <Ticket className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Create a ticket
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </Link>
        )}

        {isAdmin && (
          <Link to="/enhancement-tracker" className={linkClass}>
            <span className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Request a feature
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </Link>
        )}

        <div className="my-1 border-t" />

        <button
          type="button"
          onClick={() => onOpenSection('keyboard-shortcuts')}
          className={`w-full ${linkClass}`}
        >
          <span className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Keyboard shortcuts
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={() => onOpenSection('whats-new')}
          className={`w-full ${linkClass}`}
        >
          <span className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            What&rsquo;s new
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </button>

        {version?.version && (
          <p className="px-3 pt-3 text-xs text-muted-foreground">
            Grackle CRM v{version.version}
            {/* envName, not environment. The latter is NODE_ENV, which is 'production' on
                dev too, so this suffix never once appeared on dev - it was only ever
                visible locally. */}
            {version.envName && version.envName !== 'prod' ? ` (${version.envName})` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
